import type { MutationOptions } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isLoggedInAtom } from 'app/atoms';
import { http } from 'app/lib/axios';
import type { FunctionalUseQueryOptions } from 'app/types/network';
import type { SharingPlace, SharingPlaceMember } from 'app/types/sharing-place';
import { useAtomValue } from 'jotai';

export type { SharingPlace, SharingPlaceMember } from 'app/types/sharing-place';

const sharingPlaceQueryKeys = {
  allPlaces: () => ['sharing-places', 'all-places'] as const,
  myPlaces: () => ['sharing-places', 'my-places'] as const,
  members: (placeId: string) => ['sharing-places', placeId, 'members'] as const,
};

/**
 * Returns all sharing places the authenticated user frequents.
 */
export function useMyPlaces(opts: FunctionalUseQueryOptions<SharingPlace[]> = {}) {
  const isLoggedIn = useAtomValue(isLoggedInAtom);

  return useQuery<SharingPlace[]>({
    queryKey: sharingPlaceQueryKeys.myPlaces(),
    queryFn: () => http.get<SharingPlace[]>('/sharing-places/my-places').then((res) => res.data),
    ...opts,
    enabled: isLoggedIn && (opts.enabled ?? true),
  });
}

/**
 * Returns all sharing places near the authenticated user.
 */
export function useAllPlaces(opts: FunctionalUseQueryOptions<SharingPlace[]> = {}) {
  const isLoggedIn = useAtomValue(isLoggedInAtom);

  return useQuery<SharingPlace[]>({
    queryKey: sharingPlaceQueryKeys.allPlaces(),
    queryFn: () => http.get<SharingPlace[]>('/sharing-places/all-places').then((res) => res.data),
    ...opts,
    enabled: isLoggedIn && (opts.enabled ?? true),
  });
}

/**
 * Returns all members (users) who frequent a given sharing place.
 * Only accessible by the place owner.
 */
export function useSharingPlaceMembers(
  placeId: string | undefined,
  opts: FunctionalUseQueryOptions<SharingPlaceMember[]> = {},
) {
  const isLoggedIn = useAtomValue(isLoggedInAtom);

  return useQuery<SharingPlaceMember[]>({
    queryKey: sharingPlaceQueryKeys.members(placeId!),
    queryFn: () => http.get<SharingPlaceMember[]>(`/sharing-places/${placeId!}/members`).then((res) => res.data),
    ...opts,
    enabled: isLoggedIn && !!placeId && (opts.enabled ?? true),
  });
}

/**
 * Registers the authenticated user as frequenting a sharing place.
 * Invalidates the my-places list on success.
 */
export function useJoinPlace(opts: MutationOptions<SharingPlace, Error, string> = {}) {
  const client = useQueryClient();

  return useMutation<SharingPlace, Error, string>({
    mutationFn: (placeId) => http.post<SharingPlace>(`/sharing-places/${placeId}/join`).then((res) => res.data),
    ...opts,
    async onSuccess(result, variables, context) {
      await client.invalidateQueries({ queryKey: sharingPlaceQueryKeys.myPlaces() });
      await client.invalidateQueries({ queryKey: sharingPlaceQueryKeys.allPlaces() });
      return opts.onSuccess?.(result, variables, context);
    },
  });
}

/**
 * Unregisters the authenticated user from a sharing place.
 * Invalidates the my-places list on success.
 */
export function useLeavePlace(opts: MutationOptions<undefined, Error, string> = {}) {
  const client = useQueryClient();

  return useMutation<undefined, Error, string>({
    mutationFn: (placeId) => http.delete(`/sharing-places/${placeId}/leave`).then(() => undefined),
    ...opts,
    async onSuccess(result, variables, context) {
      await client.invalidateQueries({ queryKey: sharingPlaceQueryKeys.myPlaces() });
      await client.invalidateQueries({ queryKey: sharingPlaceQueryKeys.allPlaces() });
      return opts.onSuccess?.(result, variables, context);
    },
  });
}

/**
 * Removes a specific user from a sharing place's member list.
 * Only callable by the place owner.
 * Invalidates the members list for the given place on success.
 */
export function useRemoveSharingPlaceMember(
  opts: MutationOptions<undefined, Error, { placeId: string; memberId: string }> = {},
) {
  const client = useQueryClient();

  return useMutation<undefined, Error, { placeId: string; memberId: string }>({
    mutationFn: ({ placeId, memberId }) =>
      http.delete(`/sharing-places/${placeId}/members/${memberId}`).then(() => undefined),
    ...opts,
    async onSuccess(result, variables, context) {
      await client.invalidateQueries({ queryKey: sharingPlaceQueryKeys.members(variables.placeId) });
      return opts.onSuccess?.(result, variables, context);
    },
  });
}
